import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { techPartnerKeys } from "@/lib/query";

export interface TechPartnerMapping {
  id: string;
  product: string;
  partnerName: string;
  partnerEmail: string | null;
  createdAt: string;
}

export function useTechPartners() {
  return useQuery({
    queryKey: techPartnerKeys.all,
    queryFn: async (): Promise<TechPartnerMapping[]> => {
      const res = await fetch("/api/tech-partners");
      if (!res.ok) throw new Error("Failed to fetch tech partners");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useTechPartnerProductOptions() {
  return useQuery({
    queryKey: techPartnerKeys.productOptions,
    queryFn: async (): Promise<string[]> => {
      const res = await fetch("/api/tech-partners/product-options");
      if (!res.ok) throw new Error("Failed to fetch product options");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateTechPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { product: string; partnerName: string; partnerEmail?: string }) => {
      const res = await fetch("/api/tech-partners", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to create pairing");
      return json.data as TechPartnerMapping;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: techPartnerKeys.all });
      toast.success("Pairing created");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to create pairing");
    },
  });
}

export function useUpdateTechPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      id: string;
      product?: string;
      partnerName?: string;
      partnerEmail?: string;
    }) => {
      const { id, ...patch } = data;
      const res = await fetch(`/api/tech-partners/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to update pairing");
      return json.data as TechPartnerMapping;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: techPartnerKeys.all });
      toast.success("Pairing updated");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update pairing");
    },
  });
}

export function useDeleteTechPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/tech-partners/${id}`, {
        method: "DELETE",
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to delete pairing");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: techPartnerKeys.all });
      toast.success("Pairing deleted");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to delete pairing");
    },
  });
}
